import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { BadgeTier } from '../../components/verification';
import { trackContact } from '../../services/analytics';
import { formatAmount } from '../../utils/numberFormat';

export type BrokerType = 'individual' | 'company' | 'developer';

/**
 * Shared shape for a broker/company card in the trusted-brokers grid.
 */
export interface TrustedBroker {
  id: string;
  brokerType: BrokerType;
  name: string;
  nameAr: string;
  photo?: string;
  companyName?: string;
  companyNameAr?: string;
  companyLogo?: string;
  companySlug?: string;
  employeeCount?: number;
  branchCount?: number;
  establishedYear?: number;
  crNumber?: string;
  badgeTier: BadgeTier;
  rating: number;
  reviewCount: number;
  /** Sold + rented — deals the broker has closed. */
  transactionCount: number;
  /** Currently-active listings. */
  activeListings: number;
  yearsExperience: number;
  specialties: string[];
  city: string;
  cityAr: string;
  districts: string[];
  isOnline: boolean;
  responseRate: number;
  phone?: string;
  whatsapp?: string;
  email?: string;
}

interface BrokerCardProps {
  broker: TrustedBroker;
  isRTL: boolean;
}

const CONTACT_BUTTON =
  'relative z-20 grid h-10 w-10 shrink-0 place-items-center rounded-full border bg-[var(--pv-color-surface)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30';

/**
 * One broker / company / developer in the directory (owner, 2026-10-07), in the
 * same calm pattern as the search results: the whole card opens the profile;
 * the name carries the gold "verified" mark (THEME.md §2.4c); a stat shows only
 * when it is not zero (a wall of «0 عقار معروض · 0 صفقة» said nothing); call and
 * WhatsApp are small outline buttons, WhatsApp keeping its own green as an
 * outline, never a fill.
 */
export default function BrokerCard({ broker, isRTL }: BrokerCardProps) {
  const { t } = useTranslation('verification');
  const [logoFailed, setLogoFailed] = useState(false);
  const isCompany = broker.brokerType === 'company';
  const isDeveloper = broker.brokerType === 'developer';
  // Companies and developers are organisations: square logo + building icon.
  const isOrg = isCompany || isDeveloper;

  const displayName = isOrg
    ? (isRTL ? broker.companyNameAr || broker.companyName || broker.name : broker.companyName || broker.name)
    : (isRTL ? broker.nameAr : broker.name);

  const profileHref = isDeveloper && broker.companySlug
    ? `/developers/${broker.companySlug}`
    : isCompany && broker.companySlug
      ? `/companies/${broker.companySlug}`
      : `/broker/${broker.id}`;

  const logo = isOrg ? broker.companyLogo : broker.photo;
  const showLogo = Boolean(logo) && !logoFailed;
  const initials = (displayName || '?').charAt(0).toUpperCase();
  const callNumber = broker.phone;
  const waNumber = (broker.whatsapp || broker.phone || '').replace(/[^0-9]/g, '');
  const contactEntity = isOrg ? 'company' : 'agent';
  const verifiedLabel = t(`badges.${broker.badgeTier}`, { defaultValue: isRTL ? 'موثّق' : 'Verified' });

  // Only the stats that say something. Developers count projects, not deals.
  const stats = [
    broker.activeListings > 0
      ? `${formatAmount(broker.activeListings)} ${isRTL ? 'عقار معروض' : broker.activeListings === 1 ? 'listing' : 'listings'}`
      : '',
    broker.transactionCount > 0
      ? isDeveloper
        ? `${formatAmount(broker.transactionCount)} ${isRTL ? 'مشروع' : broker.transactionCount === 1 ? 'project' : 'projects'}`
        : `${formatAmount(broker.transactionCount)} ${isRTL ? 'صفقة منجزة' : broker.transactionCount === 1 ? 'deal closed' : 'deals closed'}`
      : '',
  ].filter(Boolean);

  return (
    <article className="group relative flex h-full flex-col rounded-[16px] border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface)] p-5 transition-shadow duration-200 hover:shadow-brand-md">
      {/* The whole card opens the profile; the contact buttons sit above this link. */}
      <Link
        to={profileHref}
        aria-label={displayName}
        className="absolute inset-0 z-10 rounded-[16px] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--pv-color-primary)]"
      />

      <div className="flex items-center gap-3">
        {showLogo ? (
          <img
            src={logo}
            alt=""
            className={
              isOrg
                ? 'h-14 w-14 shrink-0 rounded-xl border border-[var(--pv-border-soft)] bg-white object-contain p-1'
                : 'h-14 w-14 shrink-0 rounded-full object-cover'
            }
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <span
            className={`grid h-14 w-14 shrink-0 place-items-center bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)] ${
              isOrg ? 'rounded-xl' : 'rounded-full'
            }`}
            aria-hidden="true"
          >
            {isOrg ? (
              <i className={`${isDeveloper ? 'ri-building-4-line' : 'ri-building-2-line'} text-[1.375rem] leading-none`} />
            ) : (
              <span className="text-[1.25rem] font-bold">{initials}</span>
            )}
          </span>
        )}

        <div className="min-w-0 flex-1">
          <h3 className="flex items-center gap-1.5 text-[1rem] font-semibold text-[var(--pv-color-text)]">
            <span className="truncate">{displayName}</span>
            <i
              className="ri-verified-badge-fill shrink-0 text-[1.125rem] leading-none text-[#B58A0A]"
              role="img"
              aria-label={verifiedLabel}
              title={verifiedLabel}
            />
          </h3>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[0.875rem] text-[var(--pv-color-text-muted)]">
            {broker.city && (
              <span className="flex min-w-0 items-center gap-1">
                <i className="ri-map-pin-line text-base leading-none" aria-hidden="true" />
                <span className="truncate">{isRTL ? broker.cityAr : broker.city}</span>
              </span>
            )}
            {broker.rating > 0 && (
              <span className="flex items-center gap-1 tabular-nums">
                <i className="ri-star-fill text-base leading-none text-[#B58A0A]" aria-hidden="true" />
                <span className="font-semibold text-[var(--pv-color-text)]">{broker.rating.toFixed(1)}</span>
                {broker.reviewCount > 0 && <span>({formatAmount(broker.reviewCount)})</span>}
              </span>
            )}
          </p>
        </div>
      </div>

      {stats.length > 0 && (
        <p className="mt-3 text-[0.875rem] text-[var(--pv-color-text-muted)] tabular-nums">{stats.join(' · ')}</p>
      )}

      <div className="mt-auto flex items-center justify-between gap-3 pt-4">
        <span className="inline-flex items-center gap-1 text-[0.875rem] font-semibold text-[var(--pv-color-primary)]">
          {isRTL ? 'عرض الملف' : 'View profile'}
          <i className={`${isRTL ? 'ri-arrow-left-line' : 'ri-arrow-right-line'} text-base leading-none`} aria-hidden="true" />
        </span>
        <div className="flex items-center gap-2">
          {callNumber && (
            <a
              href={`tel:${callNumber}`}
              onClick={() => trackContact(contactEntity, broker.id, 'call')}
              aria-label={isRTL ? `اتصال بـ ${displayName}` : `Call ${displayName}`}
              className={`${CONTACT_BUTTON} border-[var(--pv-border-strong)] text-[var(--pv-color-primary)] hover:border-[var(--pv-color-primary)]`}
            >
              <i className="ri-phone-line text-[1.125rem] leading-none" aria-hidden="true" />
            </a>
          )}
          {waNumber && (
            <a
              href={`https://wa.me/${waNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackContact(contactEntity, broker.id, 'whatsapp')}
              aria-label={isRTL ? `واتساب ${displayName}` : `WhatsApp ${displayName}`}
              className={`${CONTACT_BUTTON} border-[#25D366]/60 text-[#1DA851] hover:border-[#25D366]`}
            >
              <i className="ri-whatsapp-line text-[1.125rem] leading-none" aria-hidden="true" />
            </a>
          )}
        </div>
      </div>
    </article>
  );
}
